import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import { ibeTransfersReportGroupRead } from 'actions/cashup/beTransfersReport.action';
import calculateTotalSumPayments from 'bl/payment/calculateTotalSumPayments.bl';
import generatePaymentsReport from 'bl/payment/generatePaymentsReport.bl';
import generatePaymentsReportFileName from 'bl/payment/generatePaymentsReportFileName.bl';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import PaymentArrival from 'components/report/payment/PaymentArrival.component';
import PaymentSource from 'components/report/payment/PaymentSource.component';
import Title from 'components/report/payment/Title.component';
import ELID from 'constants/elid.const';
import PAYMENT_GROUP_NAMES from 'constants/payment/paymentGroupName.const';
import PAYMENT_ORIGIN_ALL from 'constants/payment/paymentOriginAll.const';
import PAYMENT_ORIGIN_NAME_PDF from 'constants/payment/paymentOriginNamePdf.const';
import useAef from 'hooks/useAef.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCashupSelectedStaff } from 'selectors/cashup.selectors';
import {
  selectCurrentSalonAddress1,
  selectCurrentSalonCity,
  selectCurrentSalonCurrency,
  selectCurrentSalonId,
  selectCurrentSalonName,
  selectCurrentSalonPostcode,
} from 'selectors/currentSalon.selectors';
import getArray from 'utils/accessors/getArray.util';
import getObject from 'utils/accessors/getObject.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { formatDayToYear } from 'utils/date/format.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import isNegativeNumber from 'utils/predicates/number/isNegativeNumber.util';
import C from 'utils/values/constant.util';

import CN from './PayoutReport.module.scss';

const L = logger('PayoutReport');

const RENDER_PAYMENT_ARRIVAL = true;

// NOTE:Filtering by nonZeroAmount would exclude payments with count > 0 but all zero amounts
// Some payment might have all of fields equal to 0, but still have a 'count' (number of transactions).

// const nonZeroAmount = (p) =>
//   fromAnyToBool(p.amount || p.fee_exc_vat || p.fee_inc_vat || p.transfers || p.total_amount_paid_via_card);

const ORDERED_ORIGINS = C([
  PAYMENT_ORIGIN_ALL.slickPos,
  PAYMENT_ORIGIN_ALL.slickPosRefund,
  PAYMENT_ORIGIN_ALL.cardOnFile,
  PAYMENT_ORIGIN_ALL.cardOnFileRefund,
  PAYMENT_ORIGIN_ALL.noShowFee,
  PAYMENT_ORIGIN_ALL.noShowFeeRefund,
  PAYMENT_ORIGIN_ALL.ncobDeposit,
  PAYMENT_ORIGIN_ALL.ncobDepositRefund,
  PAYMENT_ORIGIN_ALL.slickPosDeposit,
  PAYMENT_ORIGIN_ALL.slickPosDepositRefund,
  PAYMENT_ORIGIN_ALL.slickPosVoucher,
  PAYMENT_ORIGIN_ALL.onlineVoucher,
  PAYMENT_ORIGIN_ALL.slickPosVoucherRefund,
  PAYMENT_ORIGIN_ALL.onlineVoucherRefund,
  PAYMENT_ORIGIN_ALL.refundFee,
  PAYMENT_ORIGIN_ALL.amexFee,
  PAYMENT_ORIGIN_ALL.disputeFee,
  PAYMENT_ORIGIN_ALL.tipPos,
  PAYMENT_ORIGIN_ALL.tipPosRefund,
  PAYMENT_ORIGIN_ALL.tipOnline,
  PAYMENT_ORIGIN_ALL.tipOnlineRefund,
]);

const PAYMENT_GROUPS = C({
  grouped: {
    [PAYMENT_GROUP_NAMES.cardInStore]: [
      PAYMENT_ORIGIN_ALL.slickPos,
      PAYMENT_ORIGIN_ALL.slickPosRefund,
      PAYMENT_ORIGIN_ALL.slickPosDeposit,
      PAYMENT_ORIGIN_ALL.slickPosDepositRefund,
      PAYMENT_ORIGIN_ALL.slickPosVoucher,
      PAYMENT_ORIGIN_ALL.slickPosVoucherRefund,
      PAYMENT_ORIGIN_ALL.tipPos,
      PAYMENT_ORIGIN_ALL.tipPosRefund,
    ],

    [PAYMENT_GROUP_NAMES.cardOnline]: [
      PAYMENT_ORIGIN_ALL.cardOnFile,
      PAYMENT_ORIGIN_ALL.cardOnFileRefund,
      PAYMENT_ORIGIN_ALL.ncobDeposit,
      PAYMENT_ORIGIN_ALL.ncobDepositRefund,
      PAYMENT_ORIGIN_ALL.noShowFee,
      PAYMENT_ORIGIN_ALL.noShowFeeRefund,
      PAYMENT_ORIGIN_ALL.onlineVoucher,
      PAYMENT_ORIGIN_ALL.onlineVoucherRefund,
      PAYMENT_ORIGIN_ALL.tipOnline,
      PAYMENT_ORIGIN_ALL.tipOnlineRefund,
    ],
    [PAYMENT_GROUP_NAMES.refundAmexFee]: [PAYMENT_ORIGIN_ALL.refundFee, PAYMENT_ORIGIN_ALL.amexFee, PAYMENT_ORIGIN_ALL.disputeFee],
  },
});

const COLUMN_NAMES = C({
  all: 'cashUp.payoutReport.headers.receivedInStripe',
  salon: 'cashUp.payoutReport.headers.businessShare',
  employed: 'cashUp.payoutReport.headers.businessShare',
  self: 'cashUp.payoutReport.headers.selfemployedShare',
  single: 'cashUp.payoutReport.headers.staffShare',
});

const HIDE_MODAL_PAYMENT_ORIGINS = {
  [PAYMENT_ORIGIN_ALL.slickPosDepositRefund]: true,
  [PAYMENT_ORIGIN_ALL.slickPosVoucher]: true,
  [PAYMENT_ORIGIN_ALL.slickPosVoucherRefund]: true,
  [PAYMENT_ORIGIN_ALL.ncobDepositRefund]: true,
  [PAYMENT_ORIGIN_ALL.onlineVoucher]: true,
  [PAYMENT_ORIGIN_ALL.onlineVoucherRefund]: true,
};

const groupPaymentsByOrigin = (payments) => {
  const groupedPayments = Object.entries(PAYMENT_GROUPS.grouped).map(([groupName, origins]) => ({
    groupName,
    payments: payments.filter((p) => origins.includes(p.payment_origin)),
  }));

  return { groupedPayments };
};

const byOrigin = (a, b) => {
  const c = ORDERED_ORIGINS.indexOf(a.payment_origin);
  const d = ORDERED_ORIGINS.indexOf(b.payment_origin);

  // eslint-disable-next-line no-nested-ternary
  return c > d ? 1 : c < d ? -1 : 0;
};

const Total = ({ data, t }) => {
  const { amount, total_amount_paid_via_card: totalAmountPaidViaCard, fee_inc_vat: incVat, transfers, count } = asObject(data);

  return (
    <div data-bem="Total" className={CN.gridRow}>
      <Title tag="PayoutReport.Total.first" width="first">
        {t('common.total').toUpperCase()}
      </Title>
      <Title tag="PayoutReport.Total.count">{count}</Title>
      <Title tag="PayoutReport.Total.totalAmountPaidViaCard" red={isNegativeNumber(totalAmountPaidViaCard)}>
        <CurrencySign minus="first" value={totalAmountPaidViaCard} />
      </Title>
      <Title tag="PayoutReport.Total.amount" red={isNegativeNumber(amount)}>
        <CurrencySign minus="first" value={amount} />
      </Title>
      <Title tag="PayoutReport.Total.incVat" red={isNegativeNumber(incVat)}>
        <CurrencySign minus="first" value={incVat} />
      </Title>
      <Title tag="PayoutReport.Total.transfers" red={isNegativeNumber(transfers)}>
        <CurrencySign minus="first" value={transfers} />
      </Title>
    </div>
  );
};

const GroupTotal = (options) => {
  const { groupName, payments, isExpanded, t } = options;

  const totals = { count: 0, amount: 0, totalAmountPaidViaCard: 0, incVat: 0, transfers: 0 };

  for (const { count, total_amount_paid_via_card, amount, fee_inc_vat, transfers } of payments) {
    totals.count += count || 0;
    totals.totalAmountPaidViaCard += total_amount_paid_via_card || 0;
    totals.amount += amount || 0;
    totals.incVat += fee_inc_vat || 0;
    totals.transfers += transfers || 0;
  }

  return (
    <div
      className={cn({
        [CN.gridRow]: true,
        [CN.isOpen]: isExpanded,
      })}
    >
      <Title tag="PayoutReport.Total.first" width="first" selected={isExpanded}>
        <div className={CN.firstCell}>
          <Icon tag="SetUpView__arrow" variant="roundedPlusMinusPurple" selected={!isExpanded} unselected={isExpanded} width="lg" />
          <div className={CN.groupNameSpace}>{t(groupName)}</div>
        </div>
      </Title>
      <Title tag="PayoutReport.Total.count" selected={isExpanded}>
        {totals.count}
      </Title>
      <Title tag="PayoutReport.Total.totalAmountPaidViaCard" selected={isExpanded} red={isNegativeNumber(totals.totalAmountPaidViaCard)}>
        <CurrencySign minus="first" value={totals.totalAmountPaidViaCard} />
      </Title>
      <Title tag="PayoutReport.Total.amount" selected={isExpanded} red={isNegativeNumber(totals.amount)}>
        {groupName === PAYMENT_GROUP_NAMES.refundAmexFee ? t('common.notApplicable') : <CurrencySign minus="first" value={totals.amount} />}
      </Title>

      <Title tag="PayoutReport.Total.incVat" selected={isExpanded} red={isNegativeNumber(totals.incVat)}>
        <CurrencySign minus="first" value={totals.incVat} />
      </Title>

      <Title tag="PayoutReport.Total.transfers" selected={isExpanded} red={isNegativeNumber(totals.transfers)}>
        <CurrencySign minus="first" value={totals.transfers} />
      </Title>
    </div>
  );
};

const PayoutReport = ({
  begin: bgn,
  end,
  ids,
  group,
  ibeTransfersReportGroupRead,
  permission,
  personalStylistName,
  salonId,
  salonName,
  salonAddress,
  salonCity,
  salonPostCode,
  selectedStaff,
  selectedStylistId,
  salonSign,
}) => {
  const { isDarkMode } = useTheme();
  const [expanded, setExpanded] = useState(null);
  const [isGroupExpanded, setExpandedGroup] = useState(null);
  const { t } = useTranslation();

  const { value, isLoading, error } = useAef(async () => {
    const isAll = permission === 'all';

    const options = { begin: bgn, end, ids, group, all: isAll };
    L.debug('useAef()');

    const result = await ibeTransfersReportGroupRead(options);
    const { json, receivedAt } = asObject(result);

    // NOTE: since Chrome has a bug displaying the response in the network tab on a redirect, try to display it here
    L.debug('useAef()', 'result:', result);

    return { ...json, timestamp: receivedAt };
  }, [permission, bgn, end, ids, group, ibeTransfersReportGroupRead]);

  const toggleGroup = useCallback((groupName) => void setExpandedGroup((prev) => (prev === groupName ? null : groupName)), [setExpandedGroup]);

  const handleExpand = useCallback(({ origin }) => void setExpanded(expanded === origin ? null : origin), [expanded]);

  const payments = getArray('by_origin', value);
  const hasAmexFee = payments.some((p) => p.payment_origin === PAYMENT_ORIGIN_ALL.amexFee && p.count >= 1);
  const total = getObject('total', value);
  const { timestamp } = asObject(value);
  const isFull = isFullArray(payments);

  const handleDownload = useCallback(
    (t) => {
      const salonOptions = { salonName, salonAddress, salonCity, salonPostCode, salonId, salonSign };
      const filterOptions = { bgn, end, total, timestamp, group, stylistName: selectedStaff.name, personalStylistName };

      const options = { ...salonOptions, ...filterOptions, t };

      generatePaymentsReport(options).save(generatePaymentsReportFileName({ salonId, timestamp }));
    },
    [salonName, salonAddress, salonCity, salonPostCode, salonId, salonSign, bgn, end, total, timestamp, group, selectedStaff.name, personalStylistName],
  );

  const isTheSameDay = formatDayToYear(bgn) === formatDayToYear(end);
  const isArrivalRendered = RENDER_PAYMENT_ARRIVAL && isFull && isTheSameDay;

  const totalPdfSum = calculateTotalSumPayments(
    payments.map((p) => {
      if ([PAYMENT_ORIGIN_ALL.amexFee, PAYMENT_ORIGIN_ALL.disputeFee].includes(p.payment_origin)) {
        return {
          ...p,
          amount: 0, // Exclude only Gross value
        };
      }
      return p;
    }),
  );

  if (error) L.warn('()', { isLoading, error, value });

  useMemo(
    // NOTE: this hook is being used in order to log only if/when one of the values changes, not per each render
    () =>
      void L.debug('()', {
        bgn,
        end,
        ids,
        group,
        isTheSameDay,
        isFull,
        isLoading,
        error,
        value,
        payments,
      }),
    [bgn, end, error, ids, group, isFull, isLoading, isTheSameDay, payments, value],
  );

  const { groupedPayments } = groupPaymentsByOrigin(payments);

  return (
    <div data-bem="PayoutReport" data-permission={fromAnyToTrimmed(permission)}>
      <div className={CN.scroll}>
        <div
          data-bem="PayoutReport__layout"
          className={cn({
            [CN.component]: true,
            [CN.dark]: isDarkMode,
          })}
        >
          <div data-bem="PayoutReport__constrictor" className={CN.constrictor}>
            <div data-bem="PayoutReport__titles" className={CN.gridRow}>
              <Title width="first" selected>
                {t('cashUp.payoutReport.headers.cardSlickPay')}
              </Title>
              <Title selected>{t('cashUp.payoutReport.headers.transactions')}</Title>
              <Title selected>{t('cashUp.payoutReport.headers.clientAmount')}</Title>
              <Title selected>{t(COLUMN_NAMES[group]) || t('cashUp.payoutReport.headers.amount')}</Title>
              <Title selected>{t('cashUp.payoutReport.headers.fees')}</Title>
              <Title selected>{t('cashUp.payoutReport.headers.payoutBankAccount')}</Title>
            </div>

            {isLoading ? (
              <div className={CN.spinner}>
                <InlineSpinner />
              </div>
            ) : (
              <div>
                {isFull &&
                  groupedPayments.map(({ groupName, payments }) => {
                    const isCurrentGroupExpanded = isGroupExpanded === groupName;
                    return (
                      <div
                        key={groupName}
                        data-bem="PayoutReport__payments-group"
                        className={cn({
                          [CN.active]: isCurrentGroupExpanded,
                        })}
                      >
                        <div onClick={() => toggleGroup(groupName)}>
                          <GroupTotal groupName={groupName} payments={payments} isExpanded={isCurrentGroupExpanded} t={t} />
                        </div>

                        {isCurrentGroupExpanded &&
                          payments.map((p) => (
                            <PaymentSource
                              begin={bgn}
                              end={end}
                              key={p.payment_origin}
                              payment={p}
                              isExpanded={p.payment_origin && expanded === p.payment_origin}
                              onExpand={handleExpand}
                              isGroupOpen={isCurrentGroupExpanded}
                              groupName={group}
                              selectedStylistId={selectedStylistId}
                              hideModalForSubPaymentsGroup={HIDE_MODAL_PAYMENT_ORIGINS[p.payment_origin]}
                              hasAmexFee={hasAmexFee}
                            />
                          ))}
                      </div>
                    );
                  })}
              </div>
            )}

            {isFalsy(isLoading) && <Total data={total} t={t} />}
          </div>
        </div>
        {isArrivalRendered && <PaymentArrival total={total} value={payments} date={bgn} />}
      </div>

      <div className={CN.outerWrapper}>
        {hasAmexFee && <div className={CN.amexFeeWarning}>{t('cashUp.amexFeeWarning')}</div>}

        {total.transfers ? (
          <div className={CN.downloadOuterContainer}>
            <Button variant="primary" onClick={() => handleDownload(t)} xSpace>
              <div className={CN.downloadInnerContainer}>
                <Icon variant="stripeWhite" width="xs" />
                {t('cashUp.downloadStripeFees')}
              </div>
            </Button>
          </div>
        ) : (
          <div />
        )}
      </div>

      <table data-bem="PayoutReport__pdf-table" id={ELID.payoutPdfTable} className={CN.pdfTable}>
        <thead>
          <tr>
            <th />
            <th>{t('cashUp.payoutReport.pdfTable.count')}</th>
            <th>{t('cashUp.payoutReport.pdfTable.grossValue')}</th>
            <th>{t('cashUp.payoutReport.pdfTable.netPayout')}</th>
            <th>{t('cashUp.payoutReport.pdfTable.feesExVAT')}</th>
            <th>{t('cashUp.payoutReport.pdfTable.vatOnFees')}</th>
            <th>{t('cashUp.payoutReport.pdfTable.feesIncVat')}</th>
          </tr>
        </thead>
        <tbody>
          {isFull &&
            payments.sort(byOrigin).map((p) => (
              <tr key={p.payment_origin}>
                <td>{t(PAYMENT_ORIGIN_NAME_PDF[p.payment_origin])}</td>
                <td>{p.count}</td>
                <td>
                  <CurrencySign value={p.amount} />
                </td>
                <td>
                  <CurrencySign value={p.transfers} />
                </td>
                <td>
                  <CurrencySign value={p.fee_exc_vat} />
                </td>
                <td>
                  <CurrencySign value={p.vat} />
                </td>
                <td>
                  <CurrencySign value={p.fee_inc_vat} />
                </td>
              </tr>
            ))}
          <tr>
            <td>{t('common.total')}</td>
            <td>{totalPdfSum.count}</td>
            <td>
              <CurrencySign value={totalPdfSum.amount} />
            </td>
            <td>
              <CurrencySign value={totalPdfSum.transfers} />
            </td>
            <td>
              <CurrencySign value={totalPdfSum.fee_exc_vat} />
            </td>
            <td>
              <CurrencySign value={totalPdfSum.vat} />
            </td>
            <td>
              <CurrencySign value={totalPdfSum.fee_inc_vat} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  salonName: selectCurrentSalonName(state),
  salonAddress: selectCurrentSalonAddress1(state),
  salonCity: selectCurrentSalonCity(state),
  salonPostCode: selectCurrentSalonPostcode(state),
  selectedStaff: selectCashupSelectedStaff(state),
  salonSign: selectCurrentSalonCurrency(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ibeTransfersReportGroupRead }, dispatch);

const PayoutReportHoc = connect(mapStateToProps, mapDispatchToProps)(PayoutReport);

export default PayoutReportHoc;
